import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import {
  Activity, AlertTriangle, Anchor, ArrowRight, CheckCircle, ChevronRight, Clock,
  ClipboardList, Edit3, Eye, FileText, Heart, Inbox, Loader2, Mail, Pause,
  Play, RefreshCw, Save, Send, Settings, Shield, ShieldAlert, ShieldCheck,
  Trash2, TrendingUp, User, UserX, Users, X, XCircle, Zap, Search, BarChart3,
  Gauge, Radio, Power, RotateCw, Ship, MapPin, ShieldOff, MessageSquareWarning,
  Workflow, Bot, Sparkles, FileSearch,
  LayoutDashboard, Layers, GraduationCap, ChevronDown,
} from "lucide-react";
import { API_URL } from "../config/api";
import { Navbar } from "../components/Navbar";
import {
  UnsafeRepliesTab, CorrectionsTab, FailedJobsTab, ReplayQueueTab,
  AIDraftsTab, RFQRoutingTab, TokenUsageTab, AuditExplorerTab,
  DaemonHealthTab, ExpandedMetricsPanel, EscalationV2Context,
  RoutingChangesTab, TelemetryBanner,
  PAAutomationToggles, AutoSentTodayPanel,
} from "./PAOperationsV2Tabs";

const CAT_COLORS = {
  shipment_status: "bg-blue-100 text-blue-800",
  eta_request: "bg-cyan-100 text-cyan-800",
  document_request: "bg-indigo-100 text-indigo-800",
  internal_request: "bg-slate-100 text-slate-700",
  complaint: "bg-red-100 text-red-800",
  pricing_commercial: "bg-amber-100 text-amber-800",
  booking: "bg-emerald-100 text-emerald-800",
  unknown: "bg-gray-100 text-gray-600",
};
const CAT_LABELS = {
  shipment_status: "Shipment", eta_request: "ETA", document_request: "Document",
  internal_request: "Internal", complaint: "Complaint", pricing_commercial: "Pricing",
  booking: "Booking", unknown: "Unknown",
};
const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800", drafted: "bg-indigo-100 text-indigo-800",
  approved: "bg-green-100 text-green-800", sent: "bg-emerald-100 text-emerald-800",
  escalated: "bg-red-100 text-red-800", rejected: "bg-red-50 text-red-600",
  completed: "bg-slate-100 text-slate-600", waiting_internal: "bg-purple-100 text-purple-800",
  pending_approval: "bg-orange-100 text-orange-800",
  edit_pending: "bg-sky-100 text-sky-800", ignored: "bg-gray-100 text-gray-400",
};
const RISK_COLORS = {
  high: "bg-red-100 text-red-800", medium: "bg-amber-100 text-amber-800", low: "bg-green-100 text-green-800",
};

// ─── Sidebar navigation (consolidates the flat 20-tab bar into grouped nav) ───
const NAV_GROUPS = [
  { title: null, items: [
    { id: "overview", label: "Command Center", icon: LayoutDashboard },
  ]},
  { title: "Action Center", items: [
    { id: "queue", label: "Approval Queue", icon: ShieldCheck, badge: "drafts" },
    { id: "escalations", label: "Escalations", icon: AlertTriangle, badge: "escalated" },
    { id: "actions", label: "Recent Actions", icon: Activity },
  ]},
  { title: "Inbox", items: [
    { id: "inbox", label: "Processed Emails", icon: Inbox, badge: "pending" },
    { id: "failed", label: "Failed Jobs", icon: XCircle },
    { id: "unsafe", label: "Unsafe Replies", icon: ShieldOff },
    { id: "corrections", label: "Corrections", icon: MessageSquareWarning },
    { id: "replay", label: "Replay Queue", icon: Workflow },
  ]},
  { title: "Automation", items: [
    { id: "automation", label: "Automation Levels", icon: Layers },
    { id: "health", label: "Health & Teaching", icon: Gauge },
    { id: "rules", label: "Safety Rules", icon: Shield },
  ]},
  { title: "Insights", collapsible: true, items: [
    { id: "metrics", label: "Metrics", icon: BarChart3 },
    { id: "tokens", label: "Token Usage", icon: Sparkles },
    { id: "aidrafts", label: "AI Drafts", icon: Bot },
    { id: "rfq", label: "RFQ Routing", icon: TrendingUp },
    { id: "changes", label: "Routing Changes", icon: TrendingUp },
    { id: "explorer", label: "Audit Explorer", icon: FileSearch },
    { id: "audit", label: "Audit Log", icon: ClipboardList },
  ]},
  { title: "Admin", collapsible: true, items: [
    { id: "users", label: "Users", icon: Users },
    { id: "controls", label: "Controls", icon: Settings },
    { id: "settings", label: "Ignored Senders", icon: UserX, badge: "ignored" },
  ]},
];
const SECTION_TITLES = NAV_GROUPS.reduce((acc, g) => {
  g.items.forEach(i => { acc[i.id] = i.label; });
  return acc;
}, {});
const AUTO_TIERS = [
  { n: 1, key: "ooo", label: "Out-of-Office", icon: Clock, tone: "emerald",
    desc: "Outside working hours (Mon–Fri 08:00–16:00 CET), any genuine single-recipient email gets an instant acknowledgement reply." },
  { n: 2, key: "pf_status", label: "Shipment / PF Status", icon: Ship, tone: "sky",
    desc: "Status, ETA, document and POD questions that match a Position Folder are answered with the real, live milestone data." },
  { n: 3, key: "learned", label: "Learned Templates", icon: GraduationCap, tone: "violet",
    desc: "Emails matching a rule you taught the PA are answered with your approved template." },
  { n: 4, key: "high_trust", label: "High-Trust", icon: ShieldCheck, tone: "amber",
    desc: "Safe categories the PA is ≥85% confident about. Teach the PA more and more emails graduate into this tier and start auto-sending." },
];

export default function PAOperationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [token] = useState(() => localStorage.getItem("token"));
  const headers = { Authorization: `Bearer ${token}` };
  const deepLinkHandled = useRef(false);

  // Deep link: map ?tab= values to internal tab names
  const TAB_MAP = { overview: "overview", automation: "automation", queue: "queue", escalated: "escalations", escalations: "escalations", inbox: "inbox", actions: "actions", metrics: "metrics", rules: "rules", users: "users", audit: "audit", controls: "controls", settings: "settings", unsafe: "unsafe", corrections: "corrections", failed: "failed", replay: "replay", changes: "changes", aidrafts: "aidrafts", rfq: "rfq", tokens: "tokens", explorer: "explorer", health: "health" };
  const urlTab = searchParams.get("tab");
  const urlTicket = searchParams.get("ticket");
  const urlAction = searchParams.get("action");
  const initialTab = (urlTab && TAB_MAP[urlTab]) || "overview";
  const [activeTab, setActiveTab] = useState(initialTab);

  // Data states
  const [dashboard, setDashboard] = useState(null);
  const [health, setHealth] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [recentActions, setRecentActions] = useState([]);
  const [emails, setEmails] = useState([]);
  const [queue, setQueue] = useState([]);
  const [escalations, setEscalations] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [rules, setRules] = useState(null);
  const [paUsers, setPaUsers] = useState([]);
  const [controls, setControls] = useState(null);

  // UI states
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [teachPrefill, setTeachPrefill] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [emailFilter, setEmailFilter] = useState("all");
  const [editingRules, setEditingRules] = useState(false);
  const [rulesForm, setRulesForm] = useState({});
  const [editDraft, setEditDraft] = useState(null);
  const [editBody, setEditBody] = useState("");
  const [auditSearch, setAuditSearch] = useState("");
  const [metricsDays, setMetricsDays] = useState(7);
  const [escSelected, setEscSelected] = useState(null);
  const [escReplyTo, setEscReplyTo] = useState("");
  const [escReplySubject, setEscReplySubject] = useState("");
  const [escReplyBody, setEscReplyBody] = useState("");
  const [escReplyCc, setEscReplyCc] = useState("");
  const [escReplySending, setEscReplySending] = useState(false);
  const [escNote, setEscNote] = useState("");

  // Booking approval state
  const [bookingModal, setBookingModal] = useState(null); // holds the email ticket + booking_data
  const [bookingEdits, setBookingEdits] = useState({});
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingRejReason, setBookingRejReason] = useState("");

  // Edit & Send state
  const [editSendTicket, setEditSendTicket] = useState(null);
  const [editSendNotes, setEditSendNotes] = useState("");
  const [editSendLoading, setEditSendLoading] = useState(false);

  // Ignored senders state
  const [ignoredSenders, setIgnoredSenders] = useState([]);

  const ax = useCallback((method, path, data) => {
    const opts = { headers };
    if (method === "get") return axios.get(`${API_URL}/pa${path}`, opts);
    if (method === "post") return axios.post(`${API_URL}/pa${path}`, data || {}, opts);
    if (method === "put") return axios.put(`${API_URL}/pa${path}`, data || {}, opts);
    if (method === "delete") return axios.delete(`${API_URL}/pa${path}`, opts);
    return axios.get(`${API_URL}/pa${path}`, opts);
  }, [token]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [dash, hp, met, ra, em, q, esc, aud, rul, usr, ctrl, ign] = await Promise.allSettled([
        ax("get", "/dashboard"), ax("get", "/health"), ax("get", `/metrics?days=${metricsDays}`),
        ax("get", "/recent-actions?limit=30"), ax("get", emailFilter !== "all" ? `/emails?status=${emailFilter}&limit=50` : "/emails?limit=50"),
        ax("get", "/queue"), ax("get", "/escalations"), ax("get", "/audit?limit=50"),
        ax("get", "/rules"), ax("get", "/users"), ax("get", "/controls/status"),
        ax("get", "/senders/ignored"),
      ]);
      if (dash.status === "fulfilled") setDashboard(dash.value.data);
      if (hp.status === "fulfilled") setHealth(hp.value.data);
      if (met.status === "fulfilled") setMetrics(met.value.data);
      if (ra.status === "fulfilled") setRecentActions(ra.value.data.actions || []);
      if (em.status === "fulfilled") setEmails(em.value.data.emails || []);
      if (q.status === "fulfilled") setQueue(q.value.data.drafts || []);
      if (esc.status === "fulfilled") setEscalations(esc.value.data);
      if (aud.status === "fulfilled") setAuditLogs(aud.value.data.logs || []);
      if (rul.status === "fulfilled") setRules(rul.value.data);
      if (usr.status === "fulfilled") setPaUsers(usr.value.data.users || []);
      if (ctrl.status === "fulfilled") setControls(ctrl.value.data);
      if (ign.status === "fulfilled") setIgnoredSenders(ign.value.data.senders || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [ax, emailFilter, metricsDays]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ─── Booking Approval Handlers (must be before deep link useEffect) ───
  const openBookingApproval = useCallback(async (ticketId) => {
    try {
      const res = await ax("get", `/emails/${ticketId}/booking-data`);
      const data = res.data;
      if (!data.booking_data?.pf_payload) {
        toast.error("No booking data found on this ticket");
        return;
      }
      setBookingModal(data);
      setBookingEdits({ ...data.booking_data.pf_payload });
      setBookingRejReason("");
    } catch (err) { toast.error(err.response?.data?.detail || "Error loading booking data"); }
  }, [ax]);

  const handleBookingApprove = async () => {
    if (!bookingModal) return;
    setBookingLoading(true);
    try {
      await ax("post", "/bookings/approve", {
        ticket: bookingModal.email_id,
        pf_payload: bookingEdits,
        notes: "Approved from PA dashboard",
      });
      toast.success("Booking approved — PA will create PF");
      setBookingModal(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Approval failed"); }
    setBookingLoading(false);
  };

  const handleBookingReject = async () => {
    if (!bookingModal) return;
    setBookingLoading(true);
    try {
      await ax("post", "/bookings/reject", {
        ticket: bookingModal.email_id,
        reason: bookingRejReason || "Rejected from PA dashboard",
      });
      toast.success("Booking rejected");
      setBookingModal(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Reject failed"); }
    setBookingLoading(false);
  };

  // Deep link: auto-open ticket from ?ticket= param after data loads
  useEffect(() => {
    if (deepLinkHandled.current || !urlTicket || loading) return;

    // Handle booking approval/rejection actions
    if (urlAction === "approve-pf" || urlAction === "reject-pf") {
      openBookingApproval(urlTicket);
      deepLinkHandled.current = true;
      return;
    }

    // Handle Teams card / deep link actions (spec Part B.3)
    if (urlAction && urlAction !== "edit") {
      const executeDeepLinkAction = async () => {
        const actionMap = {
          "send-draft": { method: "post", path: `/drafts/${urlTicket}/send`, successMsg: "Draft sent!", icon: "check" },
          "discard-draft": { method: "put", path: `/drafts/${urlTicket}/status`, data: { status: "rejected", acted_by: "deep_link" }, successMsg: "Draft discarded.", icon: "trash" },
          "handled": { method: "put", path: `/emails/${urlTicket}/action`, data: { action: "no_action", acted_by: "deep_link" }, successMsg: "Marked as handled.", icon: "check" },
          "ignore-sender": null, // handled separately below
        };

        const cfg = actionMap[urlAction];
        if (!cfg && urlAction !== "ignore-sender") {
          deepLinkHandled.current = true;
          return;
        }

        // Confirm before executing
        const confirmMsg = {
          "send-draft": `Send the PA draft for ticket ${urlTicket}?`,
          "discard-draft": `Discard the draft for ticket ${urlTicket}?`,
          "handled": `Mark ticket ${urlTicket} as handled?`,
          "ignore-sender": `Ignore the sender of ticket ${urlTicket}? Future emails from them will be skipped.`,
        }[urlAction] || `Execute ${urlAction} for ${urlTicket}?`;

        if (!window.confirm(confirmMsg)) {
          // User cancelled — clear params and show queue
          setSearchParams({});
          deepLinkHandled.current = true;
          return;
        }

        try {
          if (urlAction === "ignore-sender") {
            // Need to find sender email first
            const emailRes = await ax("get", `/emails/${urlTicket}`);
            const senderEmail = emailRes.data?.from_email;
            if (senderEmail) {
              await ax("post", "/senders/ignore", { email: senderEmail, reason: "Ignored via deep link", ignored_by: "deep_link" });
              toast.success(`Sender ${senderEmail} ignored.`);
            } else {
              toast.error("Could not find sender email for this ticket.");
            }
          } else {
            await ax(cfg.method, cfg.path, cfg.data);
            toast.success(cfg.successMsg);
          }
        } catch (err) {
          toast.error(err.response?.data?.detail || `Action failed: ${urlAction}`);
        }

        // Clean URL and refresh
        setSearchParams({});
        fetchAll();
        deepLinkHandled.current = true;
      };
      executeDeepLinkAction();
      return;
    }

    // ?action=edit → open edit view for this ticket
    if (urlAction === "edit") {
      setActiveTab("queue");
      fetchEmailDetail(urlTicket);
      deepLinkHandled.current = true;
      return;
    }

    const scrollToTicket = (selector) => {
      setTimeout(() => {
        const el = document.querySelector(selector);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-2", "ring-blue-400");
          setTimeout(() => el.classList.remove("ring-2", "ring-blue-400"), 3000);
        }
      }, 300);
    };

    if (activeTab === "escalations" && escalations) {
      const allEsc = [...(escalations?.escalated || []), ...(escalations?.stale_pending || [])];
      const match = allEsc.find(e => e.email_id === urlTicket);
      if (match) {
        openEscalation(urlTicket);
        scrollToTicket(`[data-ticket="${urlTicket}"]`);
        deepLinkHandled.current = true;
      }
    } else if (activeTab === "queue" && queue.length > 0) {
      const match = queue.find(d => d.email_id === urlTicket || d.draft_id === urlTicket);
      if (match) {
        scrollToTicket(`[data-ticket="${urlTicket}"]`);
      }
      deepLinkHandled.current = true;
    } else if (activeTab === "actions" && recentActions.length > 0) {
      const match = recentActions.find(a => a.email_id === urlTicket);
      if (match) {
        fetchEmailDetail(urlTicket);
        scrollToTicket(`[data-ticket="${urlTicket}"]`);
      }
      deepLinkHandled.current = true;
    } else if (activeTab === "inbox" && emails.length > 0) {
      fetchEmailDetail(urlTicket);
      scrollToTicket(`[data-ticket="${urlTicket}"]`);
      deepLinkHandled.current = true;
    } else if (activeTab === "audit" && auditLogs.length > 0) {
      scrollToTicket(`[data-ticket="${urlTicket}"]`);
      deepLinkHandled.current = true;
    }
  }, [urlTicket, urlAction, loading, activeTab, escalations, queue, emails, recentActions, auditLogs, openBookingApproval]);

  // Actions
  const handleQueueAction = async (draftId, action, editedBody) => {
    try {
      await ax("post", `/queue/${draftId}`, { action, edited_body: editedBody, acted_by: "admin" });
      toast.success(`Draft ${action}d`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleEmailAction = async (emailId, action) => {
    try {
      await ax("put", `/emails/${emailId}/action`, { action, acted_by: "admin" });
      toast.success(`Action: ${action}`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Action blocked"); }
  };

  const handleControl = async (action) => {
    try {
      await ax("post", `/controls/${action}`);
      toast.success(`PA ${action} executed`);
      fetchAll();
    } catch (err) { toast.error("Control action failed"); }
  };

  const handleAssign = async (emailId, assignedTo) => {
    try {
      await ax("put", `/escalations/${emailId}/assign?assigned_to=${encodeURIComponent(assignedTo)}`);
      toast.success("Assigned");
      fetchAll();
    } catch (err) { toast.error("Assign failed"); }
  };

  const handleResolve = async (emailId) => {
    try {
      await ax("put", `/escalations/${emailId}/resolve?notes=Resolved from dashboard`);
      toast.success("Resolved");
      fetchAll();
    } catch (err) { toast.error("Resolve failed"); }
  };

  const handleSaveRules = async () => {
    try {
      const p = {};
      if (rulesForm.never_auto_send !== undefined) p.never_auto_send = rulesForm.never_auto_send.split(",").map(s => s.trim()).filter(Boolean);
      if (rulesForm.auto_send_categories !== undefined) p.auto_send_categories = rulesForm.auto_send_categories.split(",").map(s => s.trim()).filter(Boolean);
      if (rulesForm.confidence_min !== undefined) p.confidence_min = parseFloat(rulesForm.confidence_min);
      if (rulesForm.freshness_threshold_hours !== undefined) p.freshness_threshold_hours = parseInt(rulesForm.freshness_threshold_hours);
      if (rulesForm.max_auto_sends_per_hour !== undefined) p.max_auto_sends_per_hour = parseInt(rulesForm.max_auto_sends_per_hour);
      if (rulesForm.require_approval_for !== undefined) p.require_approval_for = rulesForm.require_approval_for.split(",").map(s => s.trim()).filter(Boolean);
      await ax("put", "/rules", p);
      toast.success("Rules saved");
      setEditingRules(false);
      fetchAll();
    } catch (err) { toast.error("Error saving rules"); }
  };

  const handleToggleUser = async (email) => {
    try {
      await ax("put", `/users/${email}/toggle`);
      toast.success("Toggled");
      fetchAll();
    } catch (err) { toast.error("Toggle failed"); }
  };

  // Edit & Send: user provides rough notes, PA rewrites and sends
  const handleEditSend = async (ticket) => {
    if (!editSendNotes.trim()) { toast.error("Please enter notes for the rewrite"); return; }
    setEditSendLoading(true);
    try {
      await ax("post", `/drafts/${ticket}/edit-send`, { notes: editSendNotes, acted_by: "admin" });
      toast.success("Edit & Send submitted — PA will rewrite and send");
      setEditSendTicket(null);
      setEditSendNotes("");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Edit & Send failed"); }
    setEditSendLoading(false);
  };

  // Ignore sender: suppress all future emails from this address
  const handleIgnoreSender = async (email) => {
    if (!window.confirm(`Ignore all future emails from ${email}?\n\nPending drafts from this sender will also be removed from the queue.`)) return;
    try {
      const res = await ax("post", "/senders/ignore", { email, ignored_by: "admin" });
      toast.success(`${email} ignored (${res.data.emails_ignored || 0} pending emails removed)`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to ignore sender"); }
  };

  // Remove sender from ignore list
  const handleUnignoreSender = async (email) => {
    try {
      await ax("delete", `/senders/ignored/${encodeURIComponent(email)}`);
      toast.success(`${email} removed from ignore list`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to remove"); }
  };

  const handleReply = async (emailId) => {
    if (!escReplyBody.trim()) { toast.error("Reply body required"); return; }
    setEscReplySending(true);
    try {
      const res = await ax("post", `/emails/${emailId}/reply`, {
        to_email: escReplyTo,
        subject: escReplySubject,
        body: escReplyBody,
        cc: escReplyCc || null,
        replied_by: "admin",
      });
      if (res.data.success) {
        toast.success("Reply sent!");
        setEscReplyBody(""); setEscReplyCc("");
        // Refresh the selected email detail
        const updated = await ax("get", `/emails/${emailId}`);
        setEscSelected(updated.data);
        fetchAll();
      } else {
        toast.error(`Send failed: ${res.data.error || "Unknown"}`);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Reply failed"); }
    setEscReplySending(false);
  };

  const handleAddNote = async (emailId) => {
    if (!escNote.trim()) return;
    try {
      await ax("post", `/emails/${emailId}/note`, { text: escNote, added_by: "admin" });
      toast.success("Note added");
      setEscNote("");
      const updated = await ax("get", `/emails/${emailId}`);
      setEscSelected(updated.data);
    } catch (err) { toast.error("Note failed"); }
  };

  const openEscalation = async (emailId) => {
    try {
      const res = await ax("get", `/emails/${emailId}`);
      const em = res.data;
      setEscSelected(em);
      setEscReplyTo(em.from_email || "");
      // Build subject with reference if available
      const refTag = em.reference ? ` [${em.reference}]` : "";
      setEscReplySubject(`Re: ${em.subject || ""}${refTag}`);
      setEscReplyBody(em.proposed_reply || em.draft?.body || "");
      setEscReplyCc((em.routing_cc || []).join(", "));
      setEscNote("");
    } catch (err) { toast.error("Error loading email"); }
  };

  const handleSendSummary = async () => {
    try {
      const res = await ax("post", "/daily-summary?recipients=rok.kobal@europacific.com");
      if (res.data.email_sent) toast.success("Summary sent!");
      else toast.info("Summary generated (no email)");
    } catch (err) { toast.error("Summary failed"); }
  };

  const stats = dashboard?.stats || {};
  const isPaused = controls?.paused === true;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="pa-operations-page">
      <Navbar />
      <Tabs value={activeTab} onValueChange={setActiveTab} className="pt-16">
        <div className="flex min-h-[calc(100vh-4rem)]">
          <PASidebar
            activeTab={activeTab}
            onNavigate={setActiveTab}
            counts={{
              drafts: stats.drafts_waiting,
              escalated: escalations?.escalated_count,
              pending: stats.pending_emails,
              ignored: ignoredSenders.length,
            }}
            daemonOnline={health?.daemon_status === "online"}
            isPaused={isPaused}
          />
          <main className="flex-1 min-w-0">
            {/* Sticky top bar */}
            <div className="sticky top-16 z-20 bg-white/85 backdrop-blur border-b border-slate-200 px-6 py-3 flex items-center justify-between">
              <div>
                <h1 className="text-lg font-bold text-slate-900 tracking-tight" data-testid="pa-title">
                  {SECTION_TITLES[activeTab] || "PA Operations"}
                </h1>
                <p className="text-xs text-slate-400 mt-0.5">Control Tower — if we can't see it, we don't control it.</p>
              </div>
              <div className="flex items-center gap-2">
                {isPaused && <span className="px-2 py-1 bg-rose-100 text-rose-700 text-xs rounded-full font-medium animate-pulse">PAUSED</span>}
                <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="pa-refresh">
                  <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
                </Button>
              </div>
            </div>

            <div className="px-6 py-6 space-y-4">
              {/* ═══ OVERVIEW / COMMAND CENTER ═══ */}
              <TabsContent value="overview" className="space-y-5 mt-0">
                <HealthPanel health={health} controls={controls} onControl={handleControl} isPaused={isPaused} />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <AttentionCard label="Drafts waiting approval" value={stats.drafts_waiting} icon={ShieldCheck}
                    tone="amber" cta="Review queue" onClick={() => setActiveTab("queue")} testId="overview-attn-queue" />
                  <AttentionCard label="Escalations open" value={escalations?.escalated_count} icon={AlertTriangle}
                    tone="rose" cta="Handle escalations" onClick={() => setActiveTab("escalations")} testId="overview-attn-escalations" />
                  <AttentionCard label="Pending emails" value={stats.pending_emails} icon={Inbox}
                    tone="sky" cta="Open inbox" onClick={() => setActiveTab("inbox")} testId="overview-attn-pending" />
                </div>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  <Stat label="Pending" val={stats.pending_emails} color="amber" testId="stat-pending" />
                  <Stat label="Drafts" val={stats.drafts_waiting} color="indigo" testId="stat-drafts" />
                  <Stat label="Auto-sent 24h" val={stats.auto_sent_24h} color="emerald" testId="stat-autosent" />
                  <Stat label="Escalated" val={stats.escalated} color="red" testId="stat-escalated" />
                  <Stat label="Processed" val={stats.total_processed} color="green" testId="stat-processed" />
                  <Stat label="Total" val={stats.total_emails} color="slate" testId="stat-total" />
                </div>
                <TelemetryBanner />
                <AutoSentTodayPanel />
              </TabsContent>

              {/* ═══ AUTOMATION LEVELS ═══ */}
              <TabsContent value="automation" className="space-y-5 mt-0">
                <AutomationLevels />
                <PAAutomationToggles />
                <AutoSentTodayPanel />
              </TabsContent>

          {/* ═══ B. RECENT ACTIONS TABLE ═══ */}
          <TabsContent value="actions" className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="h-4 w-4 text-blue-600" /> Recent Actions ({recentActions.length})
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">Tap <b className="text-emerald-700">Approve as-is</b> when a reply was right, or <b className="text-blue-700">Correct</b> to fix it in <b>Teach the PA</b> — the more you teach, the more emails the PA auto-handles.</p>
              </CardHeader>
              <CardContent>
                {recentActions.length === 0 ? (
                  <Empty icon={Activity} text="No actions yet" sub="When MyClaw processes emails, actions appear here." />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="actions-table">
                      <thead>
                        <tr className="text-left text-xs text-slate-500 border-b">
                          <th className="pb-2 pr-2">Time</th>
                          <th className="pb-2 pr-2">Sender</th>
                          <th className="pb-2 pr-2">Subject</th>
                          <th className="pb-2 pr-2">Category</th>
                          <th className="pb-2 pr-2">Ref</th>
                          <th className="pb-2 pr-2">Action</th>
                          <th className="pb-2 pr-2">Conf.</th>
                          <th className="pb-2">Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentActions.map((a, i) => (
                          <tr key={i} className="border-b border-slate-50 hover:bg-blue-50/60 cursor-pointer transition-colors" onClick={() => teachFromAction(a)} data-ticket={a.email_id} data-testid={`action-row-${i}`} title="Click to teach / correct the PA on this email">
                            <td className="py-2 pr-2 text-xs text-slate-400 whitespace-nowrap">{fmtTime(a.updated_at)}</td>
                            <td className="py-2 pr-2 text-xs truncate max-w-[120px]">{a.from_email}</td>
                            <td className="py-2 pr-2 text-xs font-medium truncate max-w-[180px]">{a.subject}</td>
                            <td className="py-2 pr-2"><Badge c={CAT_COLORS[a.category]}>{CAT_LABELS[a.category] || a.category}</Badge></td>
                            <td className="py-2 pr-2 text-xs text-violet-600 font-mono">{a.reference || "—"}</td>
                            <td className="py-2 pr-2 text-xs">{a.action?.replace("_", " ")}</td>
                            <td className="py-2 pr-2 text-xs">{a.confidence ? `${Math.round(a.confidence * 100)}%` : "—"}</td>
                            <td className="py-2">
                              <div className="flex items-center gap-2">
                                <Badge c={STATUS_COLORS[a.status]}>{a.status}</Badge>
                                <button
                                  onClick={(e) => { e.stopPropagation(); approveAction(a); }}
                                  disabled={approvingId === a.email_id}
                                  data-testid={`approve-action-${i}`}
                                  title="Teach the PA this reply was right — in one tap"
                                  className="text-[11px] font-medium inline-flex items-center gap-1 px-2 py-1 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 whitespace-nowrap"
                                >
                                  {approvingId === a.email_id
                                    ? <Loader2 className="h-3 w-3 animate-spin" />
                                    : <CheckCircle className="h-3 w-3" />}
                                  Approve as-is
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); teachFromAction(a); }}
                                  data-testid={`correct-action-${i}`}
                                  title="Open Teach the PA prefilled to correct this action"
                                  className="text-[11px] font-medium inline-flex items-center gap-1 px-2 py-1 rounded-md border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 whitespace-nowrap"
                                >
                                  <Sparkles className="h-3 w-3" />
                                  Correct
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══ C. APPROVAL QUEUE ═══ */}
          <TabsContent value="queue" className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-indigo-600" /> Approval Queue ({queue.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {queue.length === 0 ? (
                  <Empty icon={CheckCircle} text="No drafts pending approval." />
                ) : (
                  <div className="space-y-4">
                    {queue.map((d) => (
                      <DraftCard key={d.draft_id} draft={d} onAction={handleQueueAction}
                        editing={editDraft === d.draft_id} editBody={editBody}
                        onStartEdit={() => { setEditDraft(d.draft_id); setEditBody(d.body || ""); }}
                        onCancelEdit={() => setEditDraft(null)}
                        onEditChange={setEditBody}
                        headers={headers}
                        highlighted={urlTicket === d.email_id || urlTicket === d.draft_id}
                        onEditSend={handleEditSend}
                        editSendTicket={editSendTicket}
                        editSendNotes={editSendNotes}
                        onEditSendNotesChange={(val) => { setEditSendNotes(val); setEditSendTicket(val === "" && editSendTicket === d.email_id ? null : d.email_id); }}
                        editSendLoading={editSendLoading}
                        onIgnoreSender={handleIgnoreSender}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══ INBOX ═══ */}
          <TabsContent value="inbox" className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Inbox className="h-4 w-4 text-amber-600" /> Inbox ({emails.length})
                  </CardTitle>
                  <div className="flex gap-1">
                    {["all", "pending", "drafted", "escalated", "sent", "completed"].map(f => (
                      <Button key={f} size="sm" variant={emailFilter === f ? "default" : "ghost"} onClick={() => setEmailFilter(f)} className="text-xs capitalize" data-testid={`filter-${f}`}>{f}</Button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {emails.length === 0 ? <Empty icon={Inbox} text="No emails in this view." /> : (
                  <div className="space-y-2">
                    {emails.map((e) => (
                      <EmailRow key={e.email_id} email={e} onClick={() => fetchEmailDetail(e.email_id)} onAction={handleEmailAction} onIgnoreSender={handleIgnoreSender} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══ ESCALATIONS — MASTER-DETAIL ═══ */}
          <TabsContent value="escalations" className="space-y-0">
            <div className="flex gap-0 border rounded-lg bg-white overflow-hidden" style={{ height: "calc(100vh - 310px)", minHeight: "500px" }}>
              {/* LEFT: LIST PANE */}
              <div className="w-[360px] flex-shrink-0 border-r flex flex-col">
                <div className="p-3 border-b bg-slate-50 flex items-center justify-between">
                  <span className="text-sm font-semibold flex items-center gap-1.5 text-red-700">
                    <AlertTriangle className="h-4 w-4" /> Escalated ({escalations?.escalated_count || 0})
                  </span>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={fetchAll}><RefreshCw className="h-3 w-3" /></Button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {[...(escalations?.escalated || []), ...(escalations?.stale_pending || [])].length === 0 ? (
                    <Empty icon={CheckCircle} text="No escalations" />
                  ) : (
                    <div>
                      {(escalations?.escalated || []).map((em) => (
                        <div key={em.email_id}
                          className={`px-3 py-2.5 border-b cursor-pointer transition-colors hover:bg-slate-50 ${escSelected?.email_id === em.email_id ? "bg-blue-50 border-l-2 border-l-blue-500" : ""}`}
                          onClick={() => openEscalation(em.email_id)}
                          data-testid={`esc-item-${em.email_id}`}
                          data-ticket={em.email_id}>
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className="font-mono text-[10px] text-slate-400">{em.email_id}</span>
                            <Badge c={CAT_COLORS[em.category]}>{CAT_LABELS[em.category]}</Badge>
                            <Badge c={STATUS_COLORS[em.status]}>{em.status}</Badge>
                            <TrustBadge email={em} />
                          </div>
                          <p className="text-sm font-medium truncate">{em.subject}</p>
                          <p className="text-xs text-slate-500 truncate">{em.from_email}</p>
                          {em.assigned_to && <p className="text-[10px] text-indigo-600 mt-0.5">{em.assigned_to}</p>}
                        </div>
                      ))}
                      {(escalations?.stale_pending || []).length > 0 && (
                        <>
                          <div className="px-3 py-1.5 bg-amber-50 text-xs text-amber-700 font-medium border-y">
                            Stale Pending ({escalations?.stale_count})
                          </div>
                          {(escalations?.stale_pending || []).map((em) => (
                            <div key={em.email_id}
                              className={`px-3 py-2.5 border-b cursor-pointer transition-colors hover:bg-slate-50 ${escSelected?.email_id === em.email_id ? "bg-blue-50 border-l-2 border-l-blue-500" : ""}`}
                              onClick={() => openEscalation(em.email_id)}
                              data-ticket={em.email_id}>
                              <div className="flex items-center gap-1.5 mb-0.5">
                                <span className="font-mono text-[10px] text-slate-400">{em.email_id}</span>
                                <Badge c={CAT_COLORS[em.category]}>{CAT_LABELS[em.category]}</Badge>
                                <Badge c="bg-amber-100 text-amber-700">stale</Badge>
                              </div>
                              <p className="text-sm font-medium truncate">{em.subject}</p>
                              <p className="text-xs text-slate-500 truncate">{em.from_email}</p>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT: READING PANE */}
              <div className="flex-1 flex flex-col overflow-hidden">
                {!escSelected ? (
                  <div className="flex-1 flex items-center justify-center text-slate-400">
                    <div className="text-center">
                      <Mail className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">Select an escalation to view details</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Header */}
                    <div className="p-4 border-b bg-slate-50 flex-shrink-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                            <span className="font-mono text-xs text-violet-600 font-bold">{escSelected.email_id}</span>
                            <Badge c={CAT_COLORS[escSelected.category]}>{CAT_LABELS[escSelected.category]}</Badge>
                            <Badge c={STATUS_COLORS[escSelected.status]}>{escSelected.status}</Badge>
                            <Badge c={RISK_COLORS[escSelected.risk_level]}>{escSelected.risk_level} risk</Badge>
                            {escSelected.confidence > 0 && <span className="text-[10px] text-slate-400">{Math.round(escSelected.confidence * 100)}% conf</span>}
                            <TrustBadge email={escSelected} />
                          </div>
                          <h3 className="text-base font-semibold">{escSelected.subject}</h3>
                          <p className="text-xs text-slate-500 mt-0.5">{escSelected.from_email} &rarr; {escSelected.to_email || "info@europacific.com"}</p>
                          {escSelected.reference && <p className="text-xs text-violet-600 mt-0.5">Ref: {escSelected.reference} ({escSelected.reference_type})</p>}
                        </div>
                        <div className="flex gap-1 flex-shrink-0 ml-3">
                          {escSelected.status !== "completed" && (
                            <>
                              <Button size="sm" variant="outline" className="text-xs h-7 text-green-600 border-green-200" onClick={() => { handleResolve(escSelected.email_id); setEscSelected(null); }} data-testid="esc-resolve">
                                <CheckCircle className="h-3 w-3 mr-1" /> Resolve
                              </Button>
                              <select className="text-xs border rounded px-2 py-1 h-7" defaultValue="" onChange={(e) => { if (e.target.value) handleAssign(escSelected.email_id, e.target.value); }}>
                                <option value="">Assign to...</option>
                                {paUsers.filter(u => u.pa_enabled).map(u => <option key={u.email} value={u.email}>{u.name}</option>)}
                              </select>
                            </>
                          )}
                        </div>
                      </div>
                      {escSelected.assigned_to && <p className="text-xs text-indigo-600 mt-1.5 flex items-center gap-1"><User className="h-3 w-3" /> Assigned to: {escSelected.assigned_to}</p>}
                    </div>

                    {/* Scrollable content */}
                    <div className="flex-1 overflow-y-auto">
                      {/* Original Email Body */}
                      <div className="p-4 border-b">
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">Original Email</p>
                        <CleanEmailBody raw={escSelected.body_full || escSelected.body_preview} />
                      </div>

                      {/* V2: Routing Decision + Blocked Rules + Audit Chain */}
                      <EscalationV2Context emailId={escSelected.email_id} />

                      {/* ERP Data */}
                      {escSelected.erp_data && (
                        <div className="p-4 border-b">
                          <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">ERP Data</p>
                          <pre className="bg-violet-50 rounded p-3 text-xs border border-violet-200 overflow-x-auto">{JSON.stringify(escSelected.erp_data, null, 2)}</pre>
                        </div>
                      )}

                      {/* Draft Reply (if exists) */}
                      {(escSelected.draft || escSelected.proposed_reply) && (
                        <div className="p-4 border-b">
                          <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium flex items-center gap-1">
                            <Sparkles className="h-3 w-3 text-amber-500" /> PA Suggested Reply
                            {escSelected.language === "sl" && <span className="text-[9px] bg-slate-100 rounded px-1 py-0.5 text-slate-500">SL</span>}
                            {escSelected.is_ooh && <span className="text-[9px] bg-indigo-100 rounded px-1 py-0.5 text-indigo-600">out of hours</span>}
                          </p>
                          <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200 text-sm whitespace-pre-wrap" data-testid="pa-suggested-reply">
                            {escSelected.draft?.body || escSelected.proposed_reply}
                          </div>
                          {escSelected.draft && (
                            <p className="text-[10px] text-slate-400 mt-1">Template: {escSelected.draft.template_used || "none"} | Risk: {escSelected.draft.risk_level}</p>
                          )}
                          {(escSelected.safety_flags?.length > 0) && (
                            <p className="text-[10px] text-rose-600 mt-1" data-testid="pa-safety-flags">⚠ Safety review: {escSelected.safety_flags.join(", ")}</p>
                          )}
                        </div>
                      )}

                      {/* Reply Composer */}
                      <div className="p-4 border-b bg-blue-50/30">
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium flex items-center gap-1"><Send className="h-3 w-3" /> Compose Reply</p>
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            <div className="flex-1">
                              <label className="text-[10px] text-slate-500">To</label>
                              <Input value={escReplyTo} onChange={e => setEscReplyTo(e.target.value)} className="text-sm h-8" data-testid="esc-reply-to" />
                            </div>
                            <div className="flex-1">
                              <label className="text-[10px] text-slate-500">CC</label>
                              <Input value={escReplyCc} onChange={e => setEscReplyCc(e.target.value)} className="text-sm h-8" placeholder="Optional" />
                            </div>
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-500">Subject</label>
                            <Input value={escReplySubject} onChange={e => setEscReplySubject(e.target.value)} className="text-sm h-8" />
                          </div>
                          <Textarea value={escReplyBody} onChange={e => setEscReplyBody(e.target.value)} rows={5}
                            placeholder="Type your reply..." className="text-sm" data-testid="esc-reply-body" />
                          {(escSelected.draft || escSelected.proposed_reply) && !escReplyBody && (
                            <Button size="sm" variant="ghost" className="text-xs text-indigo-600" data-testid="use-pa-reply" onClick={() => setEscReplyBody(escSelected.draft?.body || escSelected.proposed_reply)}>
                              Use PA suggested reply
                            </Button>
                          )}
                          <div className="flex justify-end">
                            <Button onClick={() => handleReply(escSelected.email_id)} disabled={escReplySending || !escReplyBody.trim()} className="gap-1" data-testid="esc-send-reply">
                              {escReplySending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                              Send Reply
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Add Note */}
                      <div className="p-4 border-b">
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">Internal Note</p>
                        <div className="flex gap-2">
                          <Input value={escNote} onChange={e => setEscNote(e.target.value)} placeholder="Add a note..." className="text-sm"
                            onKeyDown={e => e.key === "Enter" && handleAddNote(escSelected.email_id)} data-testid="esc-note-input" />
                          <Button size="sm" onClick={() => handleAddNote(escSelected.email_id)} disabled={!escNote.trim()}>Add</Button>
                        </div>
                      </div>

                      {/* Timeline */}
                      <div className="p-4">
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-medium">Timeline ({escSelected.events?.length || 0})</p>
                        <div className="border-l-2 border-slate-200 ml-2 space-y-3">
                          {(escSelected.events || []).map((evt, i) => (
                            <div key={i} className="pl-4 relative">
                              <div className={`absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full border-2 border-white ${
                                evt.type === "received" ? "bg-blue-500" :
                                evt.type.includes("reply") ? "bg-green-500" :
                                evt.type.includes("note") ? "bg-yellow-500" :
                                evt.type.includes("approved") ? "bg-green-500" :
                                evt.type.includes("rejected") ? "bg-red-500" :
                                evt.type.includes("escalat") ? "bg-orange-500" :
                                evt.type.includes("assigned") ? "bg-indigo-500" :
                                "bg-slate-400"
                              }`} />
                              <div className="flex items-center gap-2 text-xs">
                                <span className="text-slate-400">{fmtTime(evt.timestamp)}</span>
                                <span className="font-mono text-violet-600">{evt.type}</span>
                                {evt.acted_by && <span className="text-slate-400">by {evt.acted_by}</span>}
                              </div>
                              <p className="text-sm text-slate-700 mt-0.5">{evt.detail}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ═══ METRICS ═══ */}
          <TabsContent value="metrics" className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex gap-2">
                {[7, 14, 30].map(d => (
                  <Button key={d} size="sm" variant={metricsDays === d ? "default" : "outline"} onClick={() => setMetricsDays(d)} className="text-xs">{d}d</Button>
                ))}
              </div>
              <Button size="sm" variant="outline" onClick={handleSendSummary} className="text-xs gap-1">
                <Send className="h-3 w-3" /> Send Summary
              </Button>
            </div>
            <MetricsPanel metrics={metrics} />
            <ExpandedMetricsPanel />
          </TabsContent>

          {/* ═══ V2: UNSAFE REPLIES ═══ */}
          <TabsContent value="unsafe" className="space-y-4"><UnsafeRepliesTab /></TabsContent>

          {/* ═══ V2: REPORT CORRECTIONS ═══ */}
          <TabsContent value="corrections" className="space-y-4"><CorrectionsTab /></TabsContent>

          {/* ═══ V2: FAILED JOBS / DEAD LETTER ═══ */}
          <TabsContent value="failed" className="space-y-4"><FailedJobsTab /></TabsContent>

          {/* ═══ V2: REPLAY QUEUE ═══ */}
          <TabsContent value="replay" className="space-y-4"><ReplayQueueTab /></TabsContent>

          {/* ═══ V2: ROUTING CHANGES (Promote / Rollback) ═══ */}
          <TabsContent value="changes" className="space-y-4"><RoutingChangesTab /></TabsContent>

          {/* ═══ V2: AI DRAFTS REVIEW ═══ */}
          <TabsContent value="aidrafts" className="space-y-4"><AIDraftsTab /></TabsContent>

          {/* ═══ V2: RFQ ROUTING DASHBOARD ═══ */}
          <TabsContent value="rfq" className="space-y-4"><RFQRoutingTab /></TabsContent>

          {/* ═══ V2: TOKEN USAGE ═══ */}
          <TabsContent value="tokens" className="space-y-4"><TokenUsageTab /></TabsContent>

          {/* ═══ V2: AUDIT EXPLORER ═══ */}
          <TabsContent value="explorer" className="space-y-4"><AuditExplorerTab /></TabsContent>

          {/* ═══ V2: DAEMON HEALTH (expanded) ═══ */}
          <TabsContent value="health" className="space-y-4"><DaemonHealthTab teachPrefill={teachPrefill} /></TabsContent>

          {/* ═══ RULES ═══ */}
          <TabsContent value="rules" className="space-y-4">
            <RulesPanel rules={rules} editing={editingRules} form={rulesForm}
              onEdit={() => { setRulesForm({
                never_auto_send: rules?.never_auto_send?.join(", ") || "",
                auto_send_categories: rules?.auto_send_only_if?.categories?.join(", ") || "",
                confidence_min: String(rules?.auto_send_only_if?.confidence_min ?? 0.85),
                freshness_threshold_hours: String(rules?.freshness_threshold_hours ?? 6),
                max_auto_sends_per_hour: String(rules?.max_auto_sends_per_hour ?? 20),
                require_approval_for: rules?.require_approval_for?.join(", ") || "",
              }); setEditingRules(true); }}
              onCancel={() => setEditingRules(false)}
              onSave={handleSaveRules}
              onChange={(f, v) => setRulesForm(p => ({ ...p, [f]: v }))}
            />
          </TabsContent>

          {/* ═══ USERS ═══ */}
          <TabsContent value="users" className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-4 w-4" /> PA User Registry ({paUsers.length})
                  <span className="ml-2 text-xs font-normal text-slate-400">— controls plugin access per person, not the mailbox reply switch (see Health → PA Automation)</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {paUsers.length === 0 ? <Empty icon={Users} text="No PA users registered." sub="Sync from ERP or add manually." /> : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-slate-500 border-b">
                          <th className="pb-2">Name</th>
                          <th className="pb-2">Email</th>
                          <th className="pb-2">Role</th>
                          <th className="pb-2">Dept</th>
                          <th className="pb-2">Status</th>
                          <th className="pb-2" title="Whether this person may use the PA plugin/features. This is NOT the daemon reply switch — that lives in 'PA Automation — per mailbox' on the Health tab.">PA Access</th>
                          <th className="pb-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paUsers.map((u, i) => (
                          <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                            <td className="py-2 pr-2 font-medium">{u.name}</td>
                            <td className="py-2 pr-2 text-xs text-slate-500">{u.email}</td>
                            <td className="py-2 pr-2 text-xs">{u.role}</td>
                            <td className="py-2 pr-2 text-xs">{u.department || "—"}</td>
                            <td className="py-2 pr-2">
                              <Badge c={u.status === "active" ? "bg-green-100 text-green-700" : u.status === "limited" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}>{u.status}</Badge>
                            </td>
                            <td className="py-2 pr-2">
                              <Badge c={u.pa_enabled ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-500"}>{u.pa_enabled ? "ON" : "OFF"}</Badge>
                            </td>
                            <td className="py-2">
                              <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => handleToggleUser(u.email)}>
                                {u.pa_enabled ? <UserX className="h-3 w-3 mr-1" /> : <User className="h-3 w-3 mr-1" />}
                                {u.pa_enabled ? "Disable" : "Enable"}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══ AUDIT ═══ */}
          <TabsContent value="audit" className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ClipboardList className="h-4 w-4" /> Audit Trail
                  </CardTitle>
                  <div className="flex gap-2">
                    <Input value={auditSearch} onChange={e => setAuditSearch(e.target.value)} placeholder="Search email, shipment, user..." className="text-xs h-8 w-48" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {auditLogs.length === 0 ? <p className="text-sm text-slate-400 text-center py-6">No audit entries.</p> : (
                  <div className="space-y-0.5">
                    {auditLogs.filter(l => !auditSearch || JSON.stringify(l).toLowerCase().includes(auditSearch.toLowerCase())).map((log, i) => (
                      <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 text-xs border-b border-slate-50" data-ticket={log.email_id || ""}>
                        <span className="text-slate-400 w-32 flex-shrink-0">{fmtTime(log.timestamp)}</span>
                        <span className="font-mono text-violet-600 w-28 flex-shrink-0">{log.action}</span>
                        {log.email_id && <span className="font-mono text-slate-500 w-24 flex-shrink-0">{log.email_id}</span>}
                        <span className="text-slate-600 truncate">{log.detail}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══ CONTROLS ═══ */}
          <TabsContent value="controls" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Power className="h-4 w-4 text-slate-600" /> Manual Controls
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <ControlButton icon={isPaused ? Play : Pause} label={isPaused ? "Resume PA" : "Pause PA"}
                    desc={isPaused ? "Resume email processing" : "Stop processing new emails"}
                    color={isPaused ? "bg-green-600 hover:bg-green-700" : "bg-amber-600 hover:bg-amber-700"}
                    onClick={() => handleControl(isPaused ? "resume" : "pause")} testId="ctrl-pause" />
                  <ControlButton icon={RotateCw} label="Run One Cycle Now"
                    desc="Signal MyClaw to process inbox immediately"
                    color="bg-blue-600 hover:bg-blue-700"
                    onClick={() => handleControl("run-cycle")} testId="ctrl-cycle" />
                  <ControlButton icon={Send} label="Send Daily Summary"
                    desc="Generate and email today's PA summary"
                    color="bg-violet-600 hover:bg-violet-700"
                    onClick={handleSendSummary} testId="ctrl-summary" />
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Shield className="h-4 w-4 text-red-600" /> Lifecycle Rules (Active)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <LifecycleRule icon={UserX} text="No PA without active ERP user" />
                  <LifecycleRule icon={Power} text="Offboarding = instant PA shutdown" />
                  <LifecycleRule icon={ShieldAlert} text="No auto-send if user inactive" />
                  <LifecycleRule icon={ArrowRight} text="Pending tasks reassigned on exit" />
                  <div className="mt-3 p-3 bg-slate-50 rounded-lg border text-xs text-slate-500">
                    These rules are enforced automatically via <code className="text-violet-600">/api/pa/users/erp-sync</code> and user update endpoints. No manual override.
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ═══ IGNORED SENDERS ═══ */}
          <TabsContent value="settings" className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <UserX className="h-4 w-4 text-red-600" /> Ignored Senders ({ignoredSenders.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {ignoredSenders.length === 0 ? (
                  <Empty icon={UserX} text="No ignored senders" sub="Use the ⋯ menu on any email or draft to ignore a sender." />
                ) : (
                  <div className="space-y-2" data-testid="ignored-senders-list">
                    {ignoredSenders.map((s) => (
                      <div key={s.email} className="flex items-center justify-between p-3 rounded-lg border bg-white" data-testid={`ignored-${s.email}`}>
                        <div>
                          <p className="text-sm font-medium text-slate-700">{s.email}</p>
                          <p className="text-xs text-slate-400">
                            Ignored by {s.ignored_by} | {fmtTime(s.ignored_at)}
                            {s.reason && ` | ${s.reason}`}
                          </p>
                        </div>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => handleUnignoreSender(s.email)}
                          data-testid={`unignore-${s.email}`}>
                          <Trash2 className="h-3 w-3 mr-1" /> Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
            </div>
        {selectedEmail && <EmailDetailModal email={selectedEmail} onClose={() => setSelectedEmail(null)} onAction={handleEmailAction} />}

        {/* Booking Approval Modal */}
        {bookingModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setBookingModal(null)}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="booking-approval-modal">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-emerald-50 to-white">
                <div>
                  <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <Ship className="h-5 w-5 text-emerald-600" /> Booking Approval
                  </h2>
                  <p className="text-sm text-slate-500 mt-0.5">
                    <span className="font-mono text-emerald-700">{bookingModal.email_id}</span>
                    {" "}&mdash; from <strong>{bookingModal.from_email}</strong>
                  </p>
                  {bookingModal.subject && <p className="text-xs text-slate-400 mt-1">{bookingModal.subject}</p>}
                </div>
                <Button variant="ghost" size="sm" onClick={() => setBookingModal(null)} data-testid="booking-modal-close"><X className="h-4 w-4" /></Button>
              </div>

              {/* Scrollable Body */}
              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                {/* Parsed Summary (read-only) */}
                {bookingModal.booking_data?.parsed && (
                  <div className="bg-slate-50 border rounded-lg p-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">PA Parsed Summary</p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      {Object.entries(bookingModal.booking_data.parsed).map(([k, v]) => (
                        <div key={k} className="flex gap-2">
                          <span className="text-slate-500 capitalize">{k.replace(/_/g, " ")}:</span>
                          <span className="font-medium text-slate-800">{String(v)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Editable PF Payload */}
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-medium">PF Fields (editable before approval)</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <BookingField label="Customer Name" field="customer_name" edits={bookingEdits} setEdits={setBookingEdits} required />
                    <BookingField label="Customer Email" field="customer_email" edits={bookingEdits} setEdits={setBookingEdits} />
                    <BookingField label="Description" field="description" edits={bookingEdits} setEdits={setBookingEdits} required full />
                    <BookingField label="Service Type" field="service_type" edits={bookingEdits} setEdits={setBookingEdits} options={["FCL", "LCL", "AIR"]} />
                    <BookingField label="Direction" field="direction" edits={bookingEdits} setEdits={setBookingEdits} options={["import", "export", "trucking", "cross_trade"]} />
                    <BookingField label="Shipping Line" field="shipping_line" edits={bookingEdits} setEdits={setBookingEdits} />
                    <BookingField label="Origin Port" field="origin_port" edits={bookingEdits} setEdits={setBookingEdits} icon={<MapPin className="h-3 w-3" />} />
                    <BookingField label="Destination Port" field="destination_port" edits={bookingEdits} setEdits={setBookingEdits} icon={<MapPin className="h-3 w-3" />} />
                    <BookingField label="ETA" field="eta" edits={bookingEdits} setEdits={setBookingEdits} />
                    <BookingField label="ETD" field="etd" edits={bookingEdits} setEdits={setBookingEdits} />
                    <BookingField label="Vessel" field="vessel_name" edits={bookingEdits} setEdits={setBookingEdits} />
                    <BookingField label="Voyage" field="voyage" edits={bookingEdits} setEdits={setBookingEdits} />
                    <BookingField label="MBL Number" field="mbl_number" edits={bookingEdits} setEdits={setBookingEdits} />
                    <BookingField label="Seal Number" field="seal_number" edits={bookingEdits} setEdits={setBookingEdits} />
                    <BookingArrayField label="Containers" field="containers" edits={bookingEdits} setEdits={setBookingEdits} />
                    <BookingArrayField label="HBL Numbers" field="hbl_numbers" edits={bookingEdits} setEdits={setBookingEdits} />
                    <BookingArrayField label="Notification Emails" field="notification_emails" edits={bookingEdits} setEdits={setBookingEdits} full />
                  </div>
                </div>

                {/* Tracking Options */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-xs text-blue-600 uppercase tracking-wider mb-2 font-medium">Tracking Configuration</p>
                  <div className="flex flex-wrap gap-4">
                    <BookingCheckbox label="Enable Tracking" field="enable_tracking" edits={bookingEdits} setEdits={setBookingEdits} />
                    <BookingCheckbox label="Track Containers" field="track_containers" edits={bookingEdits} setEdits={setBookingEdits} />
                    <BookingCheckbox label="Track MBL" field="track_mbl" edits={bookingEdits} setEdits={setBookingEdits} />
                    <BookingCheckbox label="Track HBL" field="track_hbl" edits={bookingEdits} setEdits={setBookingEdits} />
                  </div>
                </div>

                {/* Rejection Reason */}
                <div>
                  <label className="text-xs text-slate-500">Rejection reason (optional)</label>
                  <Input value={bookingRejReason} onChange={e => setBookingRejReason(e.target.value)} placeholder="e.g. Missing container numbers, need clarification" className="mt-1" data-testid="booking-reject-reason" />
                </div>
              </div>

              {/* Footer Actions */}
              <div className="flex items-center justify-between px-6 py-4 border-t bg-slate-50">
                <Button variant="outline" onClick={() => setBookingModal(null)} data-testid="booking-cancel">Cancel</Button>
                <div className="flex gap-2">
                  <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={handleBookingReject} disabled={bookingLoading} data-testid="booking-reject-btn">
                    {bookingLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <X className="h-4 w-4 mr-1" />}
                    Reject
                  </Button>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleBookingApprove} disabled={bookingLoading || !bookingEdits.customer_name || !bookingEdits.description} data-testid="booking-approve-btn">
                    {bookingLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                    Approve & Create PF
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
          </main>
        </div>
      </Tabs>
    </div>
  );

  async function fetchEmailDetail(emailId) {
    try {
      const res = await ax("get", `/emails/${emailId}`);
      setSelectedEmail(res.data);
    } catch (err) { toast.error("Error loading email"); }
  }

  // Recent Action → Teach the PA (prefilled) so the user can teach/correct it.
  async function teachFromAction(a) {
    let email = {};
    try {
      const res = await ax("get", `/emails/${a.email_id}`);
      email = res.data || {};
    } catch (err) { /* fall back to row data */ }
    setTeachPrefill({
      nonce: Date.now(),
      subject: a.subject || email.subject || "",
      body: email.body_preview || email.body || "",
      category: a.category || email.category || "",
      action: teachActionFor(a.action),
      sender: a.from_email || email.from_email || "",
      reference: a.reference || email.reference || "",
      prev_action: a.action || "",
      confidence: a.confidence || 0,
      instruction: suggestInstruction(a.action, a.category, false),
    });
    setActiveTab("health");
  }

  // One-tap "Approve as-is": teaches the PA that its action was correct,
  // without opening the panel.
  async function approveAction(a) {
    setApprovingId(a.email_id);
    try {
      let email = {};
      try {
        const res = await ax("get", `/emails/${a.email_id}`);
        email = res.data || {};
      } catch (err) { /* fall back to row data */ }
      const fd = new FormData();
      fd.append("subject", a.subject || email.subject || "");
      fd.append("body", email.body_preview || email.body || "");
      fd.append("from_email", a.from_email || email.from_email || "");
      fd.append("instruction", suggestInstruction(a.action, a.category, true));
      fd.append("category", a.category || email.category || "");
      fd.append("action", teachActionFor(a.action));
      await axios.post(`${API_URL}/pa/operator/teach`, fd, { headers });
      toast.success("Approved — the PA will keep handling similar emails this way");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save. Try again.");
    } finally {
      setApprovingId(null);
    }
  }
}

// Map a Recent-Action's action to a valid Teach action (draft/escalate/skip).
function teachActionFor(action) {
  const a = (action || "").toLowerCase();
  if (a.includes("escalat")) return "escalate";
  if (a.includes("skip") || a.includes("no_action") || a.includes("ignore") || a.includes("track")) return "skip";
  return "draft"; // draft / auto_send / replied / sent → keep drafting
}

// Pre-write a teach instruction from the PA's prior action + category so the
// operator only tweaks a sentence instead of starting from a blank box.
function suggestInstruction(action, category, reinforce) {
  const CAT = {
    shipment_status: "shipment status update",
    eta_request: "ETA request",
    document_request: "document request",
    pod_request: "proof-of-delivery (POD) request",
    company_document: "company-document request",
    rfq: "rate/quote request (RFQ)",
    accounting: "accounting/invoice query",
    bank_details_request: "bank-details request",
    complaint: "complaint",
    booking: "booking request",
    pricing: "pricing question",
    relationship_engagement: "relationship/engagement email",
    internal_request: "internal request",
  };
  const phrase = CAT[category] || "message";
  const art = /^[aeiou]/i.test(phrase) ? "an" : "a";
  const act = teachActionFor(action);
  if (act === "escalate") {
    return reinforce
      ? `Correct — keep escalating ${phrase} emails like this to a human instead of auto-replying.`
      : `Escalate ${phrase} emails like this to a human — don't auto-reply.`;
  }
  if (act === "skip") {
    return reinforce
      ? `Correct — keep just tracking ${phrase} emails like this; no reply is needed.`
      : `Just track ${phrase} emails like this — no reply needed.`;
  }
  // draft
  const DRAFT_HINT = {
    eta_request: "reply with the latest ETA and confirm the vessel and PF/booking reference",
    shipment_status: "reply with the current shipment status and next milestone",
    document_request: "attach or point to the requested document and confirm the reference",
    pod_request: "attach the POD or confirm when it will be available",
    rfq: "acknowledge the RFQ and confirm we will send a quote shortly",
    booking: "confirm the booking details and next steps",
    pricing: "answer the pricing question clearly and reference the offer",
    accounting: "acknowledge the invoice query and route it to accounting",
  };
  const hint = DRAFT_HINT[category] || "draft a helpful, professional reply that answers the sender";
  return reinforce
    ? `Correct — for ${phrase} emails like this, keep drafting a reply that will ${hint}. Reference the PF/booking number.`
    : `When you get ${art} ${phrase} like this, ${hint}. Keep the tone professional and reference the PF/booking number.`;
}

// ─── Booking Approval Sub-components ───

function BookingField({ label, field, edits, setEdits, required, full, options, icon }) {
  const val = edits[field] ?? "";
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-xs text-slate-500 flex items-center gap-1">
        {icon}{label}{required && <span className="text-red-500">*</span>}
      </label>
      {options ? (
        <select value={val} onChange={e => setEdits(p => ({ ...p, [field]: e.target.value }))}
          className="w-full mt-0.5 border rounded px-2 py-1.5 text-sm" data-testid={`booking-field-${field}`}>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <Input value={val} onChange={e => setEdits(p => ({ ...p, [field]: e.target.value }))}
          className="mt-0.5 text-sm h-8" data-testid={`booking-field-${field}`} />
      )}
    </div>
  );
}

function BookingArrayField({ label, field, edits, setEdits, full }) {
  const arr = edits[field] || [];
  const val = Array.isArray(arr) ? arr.join(", ") : String(arr);
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-xs text-slate-500">{label} <span className="text-slate-400">(comma-separated)</span></label>
      <Input value={val}
        onChange={e => setEdits(p => ({ ...p, [field]: e.target.value.split(",").map(s => s.trim()).filter(Boolean) }))}
        className="mt-0.5 text-sm h-8 font-mono" data-testid={`booking-field-${field}`} />
    </div>
  );
}

function BookingCheckbox({ label, field, edits, setEdits }) {
  const checked = edits[field] !== false;
  return (
    <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`booking-check-${field}`}>
      <input type="checkbox" checked={checked}
        onChange={e => setEdits(p => ({ ...p, [field]: e.target.checked }))}
        className="rounded border-slate-300" />
      {label}
    </label>
  );
}

// ─── Sub-components ───

function HealthPanel({ health, controls, onControl, isPaused }) {
  if (!health) return null;
  const ds = health.daemon_status;
  const online = ds === "online";
  const dot = online
    ? "bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.7)]"
    : ds === "degraded"
      ? "bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.6)]"
      : "bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.6)]";
  return (
    <div className="rounded-sm bg-neutral-900 text-neutral-200 px-4 py-2.5 font-mono" data-testid="health-panel">
      <div className="flex items-center justify-between flex-wrap gap-x-6 gap-y-1.5 text-xs">
        <div className="flex items-center gap-2">
          <span className={`h-2.5 w-2.5 rounded-full ${dot} ${online ? "animate-pulse" : ""}`} />
          <span className="uppercase tracking-[0.1em] text-neutral-400">Daemon</span>
          <strong className={`uppercase ${online ? "text-emerald-400" : "text-rose-400"}`}>{ds}</strong>
          {isPaused && <span className="px-2 py-0.5 bg-rose-500/20 text-rose-300 rounded-sm uppercase tracking-wider">Paused</span>}
        </div>
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-neutral-400">
          {health.last_heartbeat && <span>heartbeat <span className="text-neutral-100">{fmtTime(health.last_heartbeat)}</span></span>}
          {health.last_inbox_check && <span>last check <span className="text-neutral-100">{fmtTime(health.last_inbox_check)}</span></span>}
          {health.last_processed_email && <span>last processed <span className="text-neutral-100">{health.last_processed_email.email_id}</span></span>}
          {health.recent_errors?.length > 0 && (
            <span className="text-rose-400 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {health.recent_errors.length} errors</span>
          )}
        </div>
      </div>
    </div>
  );
}

function MetricsPanel({ metrics }) {
  if (!metrics) return <Empty icon={BarChart3} text="Loading metrics..." />;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard label="Total Emails" val={metrics.total_emails} icon={Mail} />
        <MetricCard label="Processed" val={metrics.processed} icon={CheckCircle} />
        <MetricCard label="Auto-sent" val={metrics.auto_sent} icon={Send} />
        <MetricCard label="Drafts" val={metrics.drafts_created} icon={Edit3} />
        <MetricCard label="Skipped" val={metrics.skipped} icon={X} />
        <MetricCard label="Escalated" val={metrics.escalated} icon={AlertTriangle} sub={`${metrics.escalation_rate}%`} />
        <MetricCard label="Errors" val={metrics.errors} icon={XCircle} sub={`${metrics.error_rate}%`} />
        <MetricCard label="Period" val={`${metrics.period_days}d`} icon={Clock} />
      </div>
      {/* Daily breakdown */}
      {metrics.daily_breakdown && Object.keys(metrics.daily_breakdown).length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Daily Volume</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-24">
              {Object.entries(metrics.daily_breakdown).map(([date, count]) => {
                const max = Math.max(...Object.values(metrics.daily_breakdown), 1);
                const h = Math.max((count / max) * 100, 4);
                return (
                  <div key={date} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-[9px] text-slate-500">{count}</span>
                    <div className="w-full bg-violet-400 rounded-t" style={{ height: `${h}%` }} title={`${date}: ${count}`} />
                    <span className="text-[8px] text-slate-400">{date.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
      {/* Category breakdown */}
      {metrics.by_category && Object.keys(metrics.by_category).length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">By Category</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {Object.entries(metrics.by_category).map(([cat, count]) => (
                <div key={cat} className="flex items-center justify-between text-sm">
                  <Badge c={CAT_COLORS[cat]}>{CAT_LABELS[cat] || cat}</Badge>
                  <span className="font-mono font-medium">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DraftCard({ draft, onAction, editing, editBody, onStartEdit, onCancelEdit, onEditChange, headers, highlighted, onEditSend, editSendTicket, editSendNotes, onEditSendNotesChange, editSendLoading, onIgnoreSender }) {
  const info = draft.email_info || {};
  const [erpStatus, setErpStatus] = useState(null);
  const [erpLoading, setErpLoading] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const loadErp = async () => {
    if (!info.reference) return;
    setErpLoading(true);
    try {
      const res = await axios.get(`${API_URL}/pa/erp/find-shipment?reference=${encodeURIComponent(info.reference)}`, { headers });
      setErpStatus(res.data);
    } catch (err) { toast.error("ERP lookup failed"); }
    setErpLoading(false);
  };

  const isEditSendOpen = editSendTicket === draft.email_id;

  return (
    <div className={`border border-l-4 rounded-sm p-4 bg-white transition-colors hover:bg-neutral-50/40 ${draft.risk_level === "high" ? "border-l-rose-500" : draft.risk_level === "medium" ? "border-l-amber-500" : "border-l-emerald-500"} ${highlighted ? "ring-2 ring-blue-400" : ""}`} data-testid={`draft-${draft.draft_id}`} data-ticket={draft.email_id}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <span className="font-mono text-xs text-slate-400">{draft.draft_id}</span>
          <div className="flex gap-1 mt-0.5">
            <Badge c={CAT_COLORS[info.category]}>{CAT_LABELS[info.category] || info.category}</Badge>
            <Badge c={RISK_COLORS[draft.risk_level]}>{draft.risk_level} risk</Badge>
            {info.reference && <span className="text-xs text-violet-600 px-1.5 py-0.5 bg-violet-50 rounded">Ref: {info.reference}</span>}
            <TrustBadge email={{ ...info, email_id: draft.email_id }} />
          </div>
        </div>
        <div className="flex items-center gap-1">
          {draft.template_used && <span className="text-[10px] text-slate-400">tmpl: {draft.template_used}</span>}
          {/* More dropdown */}
          <div className="relative">
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setShowMore(!showMore)} data-testid={`more-${draft.draft_id}`}>
              <span className="text-slate-400 text-lg leading-none">&#8943;</span>
            </Button>
            {showMore && (
              <div className="absolute right-0 top-8 z-10 bg-white border rounded-lg shadow-lg py-1 w-48" onMouseLeave={() => setShowMore(false)}>
                <button className="w-full text-left px-3 py-2 text-xs hover:bg-red-50 text-red-600 flex items-center gap-2"
                  data-testid={`ignore-sender-${draft.draft_id}`}
                  onClick={() => { setShowMore(false); onIgnoreSender(info.from_email); }}>
                  <UserX className="h-3 w-3" /> Ignore Sender
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Original email */}
      <div className="bg-slate-50 rounded p-2 mb-2 border">
        <p className="text-xs text-slate-500 mb-1">Original email:</p>
        <p className="text-sm font-medium">{info.subject}</p>
        <p className="text-xs text-slate-500">From: {info.from_email}</p>
      </div>

      {/* ERP status lookup */}
      {info.reference && (
        <div className="mb-2">
          {!erpStatus ? (
            <Button size="sm" variant="outline" className="text-xs gap-1" onClick={loadErp} disabled={erpLoading}>
              {erpLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />} Load ERP Status
            </Button>
          ) : (
            <div className="bg-violet-50 rounded p-2 border border-violet-200 text-xs">
              <p className="font-medium text-violet-700 mb-1">ERP: {erpStatus.count} result(s)</p>
              {erpStatus.results?.slice(0, 2).map((r, i) => (
                <p key={i}>{r.pf_number || r.container_number}: {r.status} {r.eta ? `| ETA: ${r.eta}` : ""}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Draft body — AI voice, visually distinct */}
      <p className="text-[10px] text-blue-600 uppercase tracking-[0.1em] font-medium mb-1 flex items-center gap-1"><Bot className="h-3 w-3" /> AI suggested reply</p>
      {editing ? (
        <div className="space-y-2 mb-3">
          <Textarea value={editBody} onChange={e => onEditChange(e.target.value)} rows={5} className="text-sm" />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => { onAction(draft.draft_id, "edit", editBody); onCancelEdit(); }}><Save className="h-3 w-3 mr-1" /> Save</Button>
            <Button size="sm" variant="ghost" onClick={onCancelEdit}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="bg-blue-50/40 border-l-2 border-l-blue-400 rounded-sm p-3 text-sm mb-3 whitespace-pre-wrap max-h-36 overflow-y-auto text-neutral-700" data-testid={`draft-body-${draft.draft_id}`}>{draft.body}</div>
      )}

      {/* Edit & Send inline area */}
      {isEditSendOpen && (
        <div className="bg-sky-50 rounded-lg p-3 border border-sky-200 mb-3 space-y-2" data-testid={`edit-send-area-${draft.email_id}`}>
          <p className="text-xs font-medium text-sky-700">Rewrite & Send — type rough notes below:</p>
          <Textarea
            value={editSendNotes}
            onChange={e => onEditSendNotesChange(e.target.value)}
            placeholder='e.g. "eta april 15, cosco faith, apologize delay"'
            rows={3}
            className="text-sm bg-white"
            data-testid={`edit-send-notes-${draft.email_id}`}
          />
          <div className="flex gap-2">
            <Button size="sm" className="bg-sky-600 hover:bg-sky-700" disabled={editSendLoading || !editSendNotes.trim()}
              onClick={() => onEditSend(draft.email_id)}
              data-testid={`rewrite-send-${draft.email_id}`}>
              {editSendLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />} Rewrite & Send
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onEditSendNotesChange("")}>Cancel</Button>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Button size="sm" className="bg-green-600 hover:bg-green-700 flex-1" onClick={() => onAction(draft.draft_id, "approve")} data-testid={`approve-${draft.draft_id}`}>
          <CheckCircle className="h-3 w-3 mr-1" /> Approve & Send
        </Button>
        <Button size="sm" variant="outline" onClick={onStartEdit} data-testid={`edit-${draft.draft_id}`}>
          <Edit3 className="h-3 w-3 mr-1" /> Edit
        </Button>
        <Button size="sm" variant="outline" className="border-sky-300 text-sky-600 hover:bg-sky-50"
          onClick={() => { onEditSendNotesChange(""); /* toggle */ }}
          data-testid={`edit-send-toggle-${draft.draft_id}`}
          title="Edit & Send: type rough notes, PA rewrites via AI and sends">
          <Edit3 className="h-3 w-3" />
        </Button>
        <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => onAction(draft.draft_id, "reject")} data-testid={`reject-${draft.draft_id}`}>
          <XCircle className="h-3 w-3 mr-1" /> Reject
        </Button>
      </div>

      <ReplyPreview draftId={draft.draft_id} headers={headers} />
    </div>
  );
}

function ReplyPreview({ draftId, headers }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (data) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/pa/queue/${draftId}/preview`, { headers });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load reply preview");
    } finally { setLoading(false); }
  };
  return (
    <div className="mt-2" data-testid={`reply-preview-${draftId}`}>
      <button onClick={toggle} className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1" data-testid={`reply-preview-toggle-${draftId}`}>
        <Eye className="h-3 w-3" /> {open ? "Hide" : "Preview"} exact reply (as it will be sent)
      </button>
      {open && (
        <div className="mt-2 border rounded-sm overflow-hidden" data-testid={`reply-preview-panel-${draftId}`}>
          {loading ? (
            <div className="p-3 text-xs text-slate-400">Loading…</div>
          ) : data ? (
            <>
              <div className="bg-neutral-50 px-3 py-2 text-xs border-b space-y-0.5">
                <p><span className="text-slate-400">From:</span> <b>{data.from_name}</b> &lt;{data.from_email}&gt;</p>
                <p><span className="text-slate-400">To:</span> {data.to_email}</p>
                {data.cc?.length > 0 && <p><span className="text-slate-400">Cc:</span> {data.cc.join(", ")}</p>}
                <p><span className="text-slate-400">Subject:</span> {data.subject}</p>
              </div>
              <div className="p-3 max-h-80 overflow-y-auto bg-white text-sm" dangerouslySetInnerHTML={{ __html: data.html }} />
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

function EmailRow({ email, onClick, onAction, onIgnoreSender }) {
  const [showMore, setShowMore] = useState(false);
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border hover:bg-slate-50 transition-colors cursor-pointer" onClick={onClick} data-testid={`email-${email.email_id}`} data-ticket={email.email_id}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-1 flex-wrap">
          <span className="font-mono text-[10px] text-slate-400">{email.email_id}</span>
          <Badge c={CAT_COLORS[email.category]}>{CAT_LABELS[email.category] || email.category}</Badge>
          <Badge c={STATUS_COLORS[email.status]}>{email.status}</Badge>
          <TrustBadge email={email} />
          {email.confidence > 0 && <span className="text-[10px] text-slate-400">{Math.round(email.confidence * 100)}%</span>}
          {email.reference && <span className="text-[10px] bg-violet-50 text-violet-600 px-1 py-0.5 rounded">Ref: {email.reference}</span>}
        </div>
        <p className="text-sm font-medium truncate">{email.subject}</p>
        <p className="text-xs text-slate-500">{email.from_email} | {fmtTime(email.created_at)}</p>
      </div>
      <div className="flex gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
        {email.status === "pending" && (
          <>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600" onClick={() => onAction(email.email_id, "escalate")}><AlertTriangle className="h-3 w-3" /></Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onAction(email.email_id, "no_action")}><X className="h-3 w-3" /></Button>
          </>
        )}
        <div className="relative">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setShowMore(!showMore)}>
            <span className="text-slate-400 text-lg leading-none">&#8943;</span>
          </Button>
          {showMore && (
            <div className="absolute right-0 top-8 z-10 bg-white border rounded-lg shadow-lg py-1 w-48" onMouseLeave={() => setShowMore(false)}>
              <button className="w-full text-left px-3 py-2 text-xs hover:bg-red-50 text-red-600 flex items-center gap-2"
                data-testid={`ignore-sender-email-${email.email_id}`}
                onClick={() => { setShowMore(false); onIgnoreSender(email.from_email); }}>
                <UserX className="h-3 w-3" /> Ignore Sender
              </button>
            </div>
          )}
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
    </div>
  );
}

function EscalationRow({ email, onAssign, onResolve, users }) {
  const [assigning, setAssigning] = useState(false);
  return (
    <div className="p-3 rounded-lg border hover:bg-slate-50" data-testid={`esc-${email.email_id}`}>
      <div className="flex items-center gap-1.5 mb-1 flex-wrap">
        <span className="font-mono text-[10px] text-slate-400">{email.email_id}</span>
        <Badge c={CAT_COLORS[email.category]}>{CAT_LABELS[email.category] || email.category}</Badge>
        <Badge c={STATUS_COLORS[email.status]}>{email.status}</Badge>
        <TrustBadge email={email} />
      </div>
      <p className="text-sm font-medium truncate">{email.subject}</p>
      <p className="text-xs text-slate-500">{email.from_email} | {fmtTime(email.created_at)}</p>
      {email.assigned_to && <p className="text-xs text-indigo-600 mt-1">Assigned to: {email.assigned_to}</p>}
      <div className="flex gap-1 mt-2">
        {assigning ? (
          <select className="text-xs border rounded px-2 py-1" onChange={(e) => { if (e.target.value) { onAssign(email.email_id, e.target.value); setAssigning(false); } }}>
            <option value="">Select user...</option>
            {users.filter(u => u.pa_enabled).map(u => <option key={u.email} value={u.email}>{u.name}</option>)}
          </select>
        ) : (
          <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setAssigning(true)}>
            <User className="h-3 w-3 mr-1" /> Assign
          </Button>
        )}
        <Button size="sm" variant="outline" className="text-xs h-7 text-green-600 border-green-200" onClick={() => onResolve(email.email_id)}>
          <CheckCircle className="h-3 w-3 mr-1" /> Resolve
        </Button>
      </div>
    </div>
  );
}

function EmailDetailModal({ email, onClose, onAction }) {
  const events = email.events || [];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose} data-testid="email-detail-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-3 border-b bg-slate-50">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm font-bold text-violet-600">{email.email_id}</span>
            <Badge c={CAT_COLORS[email.category]}>{CAT_LABELS[email.category]}</Badge>
            <Badge c={STATUS_COLORS[email.status]}>{email.status}</Badge>
            <Badge c={RISK_COLORS[email.risk_level]}>{email.risk_level}</Badge>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-200 rounded-lg"><X className="h-5 w-5" /></button>
        </div>
        <div className="overflow-y-auto max-h-[calc(85vh-56px)] px-6 py-4 space-y-4">
          <div>
            <p className="text-base font-medium">{email.subject}</p>
            <p className="text-xs text-slate-500">{email.from_email} &rarr; {email.to_email || "—"} | Confidence: {Math.round((email.confidence || 0) * 100)}%</p>
          </div>
          {email.body_preview && <div className="bg-slate-50 rounded-lg p-3 text-sm border whitespace-pre-wrap">{email.body_preview}</div>}
          {email.draft && (
            <div><p className="text-xs text-slate-500 font-medium mb-1">Draft Reply ({email.draft.status}):</p>
              <div className="bg-indigo-50 rounded-lg p-3 text-sm border border-indigo-200 whitespace-pre-wrap">{email.draft.body}</div>
            </div>
          )}
          {email.erp_data && <div><p className="text-xs text-slate-500 mb-1">ERP Data:</p><pre className="bg-violet-50 rounded p-2 text-xs border overflow-x-auto">{JSON.stringify(email.erp_data, null, 2)}</pre></div>}
          {email.status === "pending" && (
            <div className="flex gap-2">
              <Button size="sm" onClick={() => { onAction(email.email_id, "escalate"); onClose(); }}>Escalate</Button>
              <Button size="sm" variant="outline" onClick={() => { onAction(email.email_id, "no_action"); onClose(); }}>No Action</Button>
            </div>
          )}
          {events.length > 0 && (
            <div><p className="text-xs text-slate-500 font-medium mb-2">Timeline</p>
              <div className="border-l-2 border-slate-200 ml-2 space-y-2">
                {events.map((evt, i) => (
                  <div key={i} className="pl-4 relative text-xs">
                    <div className={`absolute -left-[5px] top-1 w-2.5 h-2.5 rounded-full border-2 border-white ${evt.type.includes("approved") ? "bg-green-500" : evt.type.includes("rejected") ? "bg-red-500" : evt.type.includes("escalat") ? "bg-orange-500" : "bg-slate-400"}`} />
                    <span className="text-slate-400">{fmtTime(evt.timestamp)}</span> <span className="font-mono text-violet-600">{evt.type}</span>
                    <p className="text-slate-600">{evt.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RulesPanel({ rules, editing, form, onEdit, onCancel, onSave, onChange }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><Shield className="h-4 w-4 text-violet-600" /> Safety Rules</CardTitle>
          {!editing ? <Button size="sm" variant="outline" onClick={onEdit} data-testid="edit-rules"><Edit3 className="h-3 w-3 mr-1" /> Edit</Button> :
            <div className="flex gap-2"><Button size="sm" onClick={onSave} data-testid="save-rules"><Save className="h-3 w-3 mr-1" /> Save</Button><Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button></div>}
        </div>
      </CardHeader>
      <CardContent>
        {rules ? (editing ? (
          <div className="space-y-3">
            <RField label="Never Auto-send (categories)" value={form.never_auto_send} onChange={v => onChange("never_auto_send", v)} hint="Comma-separated" />
            <RField label="Auto-send Categories" value={form.auto_send_categories} onChange={v => onChange("auto_send_categories", v)} hint="Comma-separated" />
            <RField label="Min Confidence" value={form.confidence_min} onChange={v => onChange("confidence_min", v)} />
            <RField label="Freshness Threshold (h)" value={form.freshness_threshold_hours} onChange={v => onChange("freshness_threshold_hours", v)} />
            <RField label="Max Auto-sends/hour" value={form.max_auto_sends_per_hour} onChange={v => onChange("max_auto_sends_per_hour", v)} />
            <RField label="Require Approval For" value={form.require_approval_for} onChange={v => onChange("require_approval_for", v)} hint="Comma-separated" />
          </div>
        ) : (
          <div className="space-y-2">
            <RRow label="Never Auto-send" value={rules.never_auto_send?.join(", ")} hl="red" />
            <RRow label="Auto-send Categories" value={rules.auto_send_only_if?.categories?.join(", ")} />
            <RRow label="Min Confidence" value={rules.auto_send_only_if?.confidence_min} />
            <RRow label="ERP Data Fresh Required" value={rules.auto_send_only_if?.erp_data_fresh ? "Yes" : "No"} />
            <RRow label="Non-committal Language" value={rules.always_use_noncommittal_language ? "Enforced" : "Off"} />
            <RRow label="Freshness Threshold" value={`${rules.freshness_threshold_hours || 6}h`} />
            <RRow label="Max Auto-sends/hour" value={rules.max_auto_sends_per_hour || 20} />
            <RRow label="Require Approval For" value={rules.require_approval_for?.join(", ")} hl="amber" />
          </div>
        )) : <p className="text-sm text-slate-400">Loading...</p>}
      </CardContent>
    </Card>
  );
}

// ─── Tiny helpers ───
function PASidebar({ activeTab, onNavigate, counts, daemonOnline, isPaused }) {
  const [collapsed, setCollapsed] = useState({});
  return (
    <aside data-testid="pa-sidebar"
      className="hidden md:flex w-60 shrink-0 flex-col sticky top-16 z-30 h-[calc(100vh-4rem)] overflow-y-auto bg-neutral-950 text-neutral-300 border-r border-neutral-800">
      <div className="px-4 py-4 border-b border-neutral-800 flex items-center gap-2.5">
        <div className="h-8 w-8 rounded-sm bg-white text-neutral-950 flex items-center justify-center">
          <Zap className="h-4 w-4" />
        </div>
        <div>
          <p className="text-sm font-bold text-white tracking-tight leading-none">PA Operations</p>
          <p className="text-[9px] uppercase tracking-[0.25em] text-neutral-500 mt-1.5">Control Tower</p>
        </div>
      </div>
      <div className="px-4 py-2.5 border-b border-neutral-800 flex items-center gap-2 text-xs font-mono">
        <span className={`h-2 w-2 rounded-full ${isPaused ? "bg-rose-500" : daemonOnline ? "bg-emerald-400 animate-pulse" : "bg-neutral-600"}`} />
        <span className="uppercase tracking-wider text-neutral-400">
          {isPaused ? "Paused" : daemonOnline ? "Daemon online" : "Daemon offline"}
        </span>
      </div>
      <nav className="flex-1 py-2 pb-28">
        {NAV_GROUPS.map((g, gi) => {
          const isColl = g.title && collapsed[g.title];
          return (
            <div key={gi} className="mb-0.5">
              {g.title && (g.collapsible ? (
                <button onClick={() => setCollapsed(p => ({ ...p, [g.title]: !p[g.title] }))}
                  className="w-full flex items-center justify-between px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-neutral-500 hover:text-neutral-300 transition-colors">
                  {g.title}
                  <ChevronDown className={`h-3 w-3 transition-transform ${isColl ? "-rotate-90" : ""}`} />
                </button>
              ) : (
                <p className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-neutral-500">{g.title}</p>
              ))}
              {!isColl && g.items.map(item => {
                const active = activeTab === item.id;
                const Icon = item.icon;
                const badgeVal = item.badge ? counts[item.badge] : 0;
                return (
                  <button key={item.id} onClick={() => onNavigate(item.id)} data-testid={`pa-nav-${item.id}`}
                    className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm border-l-2 transition-colors ${active
                      ? "bg-neutral-800 text-white border-white font-medium"
                      : "border-transparent text-neutral-400 hover:text-white hover:bg-neutral-900"}`}>
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate flex-1 text-left">{item.label}</span>
                    {badgeVal > 0 && (
                      <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-rose-500 text-white leading-none">{badgeVal}</span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}

function AttentionCard({ label, value, icon: Icon, tone, cta, onClick, testId }) {
  const tones = {
    amber: { ring: "hover:border-amber-300", num: "text-amber-600", icon: "text-amber-500" },
    rose: { ring: "hover:border-rose-300", num: "text-rose-600", icon: "text-rose-500" },
    sky: { ring: "hover:border-sky-300", num: "text-sky-600", icon: "text-sky-500" },
  };
  const t = tones[tone] || tones.sky;
  const n = value ?? 0;
  return (
    <button onClick={onClick} data-testid={testId}
      className={`text-left bg-white border border-slate-200 rounded-lg p-4 transition-colors ${t.ring} group focus:outline-none focus:ring-2 focus:ring-slate-300`}>
      <div className="flex items-start justify-between">
        <p className="text-xs uppercase tracking-[0.12em] text-slate-500 font-medium">{label}</p>
        <Icon className={`h-5 w-5 ${t.icon}`} />
      </div>
      <p className={`text-4xl font-light font-mono tracking-tighter mt-2 ${n > 0 ? t.num : "text-slate-300"}`}>{n}</p>
      <span className={`inline-flex items-center gap-1 text-xs mt-3 ${n > 0 ? "text-slate-600" : "text-slate-400"} group-hover:gap-1.5 transition-all`}>
        {cta} <ArrowRight className="h-3 w-3" />
      </span>
    </button>
  );
}

function AutomationLevels() {
  const toneMap = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    sky: "border-sky-200 bg-sky-50 text-sky-700",
    violet: "border-violet-200 bg-violet-50 text-violet-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
  };
  return (
    <Card data-testid="automation-levels">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Layers className="h-4 w-4 text-violet-600" /> Automation Levels
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-600 leading-relaxed">
          Auto-send is <b>OFF by default</b>. When you switch it ON for a mailbox below, the PA only sends
          replies that fall into one of these trusted tiers — <b>everything else is held in the Approval
          Queue</b> for you to review or teach.
        </p>
        <div className="space-y-2">
          {AUTO_TIERS.map(t => {
            const Icon = t.icon;
            return (
              <div key={t.key} data-testid={`auto-tier-${t.key}`}
                className="flex items-start gap-3 rounded-lg border border-slate-200 p-3">
                <div className={`h-8 w-8 shrink-0 rounded-sm border flex items-center justify-center font-mono text-sm font-bold ${toneMap[t.tone]}`}>{t.n}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Icon className="h-4 w-4 text-slate-500" />
                    <span className="text-sm font-semibold text-slate-800">{t.label}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 uppercase tracking-wider">Auto-sends</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">{t.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-start gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3" data-testid="auto-tier-held">
          <div className="h-8 w-8 shrink-0 rounded-sm border border-slate-300 bg-white flex items-center justify-center">
            <Pause className="h-4 w-4 text-slate-400" />
          </div>
          <div>
            <span className="text-sm font-semibold text-slate-700">Everything else → held for you</span>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">
              RFQs / pricing, complaints, multi-recipient threads and anything with a safety flag are never
              auto-sent — they wait in the Approval Queue. <b>Teach the PA</b> (Health &amp; Teaching) to turn
              more of these into High-Trust auto-sends over time.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({ label, val, color, testId }) {
  const c = color === "red" ? "text-rose-600"
    : color === "amber" ? "text-amber-600"
    : color === "emerald" || color === "green" ? "text-emerald-600"
    : color === "indigo" ? "text-indigo-600"
    : "text-neutral-900";
  return (
    <Card data-testid={testId} className="rounded-sm border-neutral-200 shadow-none hover:border-neutral-300 transition-colors">
      <CardContent className="p-3">
        <p className="text-[10px] text-neutral-500 uppercase tracking-[0.1em] font-medium">{label}</p>
        <p className={`text-2xl md:text-3xl font-light font-mono tracking-tighter mt-1 ${c}`}>{val ?? 0}</p>
      </CardContent>
    </Card>
  );
}
function Tab({ value, icon: Icon, label, badge }) {
  return (
    <TabsTrigger value={value} className="gap-1.5 text-xs rounded-sm data-[state=active]:bg-neutral-900 data-[state=active]:text-white data-[state=active]:shadow-none" data-testid={`pa-tab-${value}`}>
      <Icon className="h-3.5 w-3.5" /> {label}
      {badge > 0 && <span className="ml-0.5 px-1.5 py-0.5 text-[10px] bg-rose-500 text-white rounded-full leading-none">{badge}</span>}
    </TabsTrigger>
  );
}
function decodeEntities(s) {
  if (!s) return "";
  const t = document.createElement("textarea");
  t.innerHTML = s;
  return t.value;
}

function formatEmailText(raw) {
  if (!raw) return "";
  let t = decodeEntities(String(raw));
  // If the body still contains HTML, turn block/line tags into newlines then strip tags.
  if (/<[a-z!/][\s\S]*?>/i.test(t)) {
    t = t
      .replace(/<\s*br\s*\/?>/gi, "\n")
      .replace(/<\/\s*(p|div|tr|li|h[1-6]|table|blockquote)\s*>/gi, "\n")
      .replace(/<[^>]+>/g, "");
    t = decodeEntities(t);
  }
  t = t
    .replace(/\r\n?/g, "\n")
    .replace(/\u00a0/g, " ")                 // nbsp -> space
    // Many stored emails have their line breaks flattened into runs of spaces
    // (HTML block layout lost). Reconstruct breaks from 2+ space runs.
    .replace(/[ \t]{2,}/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  return t;
}

function CleanEmailBody({ raw }) {
  const [showAll, setShowAll] = useState(false);
  const decoded = formatEmailText(raw);
  const markers = [
    /\n-{2,}\s*Original Message/i, /\nFrom:\s/i, /\nDa:\s/i, /\nVon:\s/i,
    /\nDe:\s/i, /\nOn .+ wrote:/i, /\n_{5,}/, /\nSent:\s/i, /\nInviato:\s/i,
    /\n\[EXTERNAL EMAIL\]/i,
  ];
  let cut = -1;
  for (const m of markers) {
    const idx = decoded.search(m);
    if (idx > 40 && (cut === -1 || idx < cut)) cut = idx;
  }
  const head = cut > -1 ? decoded.slice(0, cut).trim() : decoded;
  const rest = cut > -1 ? decoded.slice(cut).trim() : "";
  return (
    <div>
      <div className="bg-white rounded-lg p-4 border text-sm text-slate-800 whitespace-pre-wrap leading-relaxed break-words" data-testid="escalation-email-body">
        {head || decoded || "(No content available)"}
      </div>
      {rest && (
        <div className="mt-2">
          <button onClick={() => setShowAll(v => !v)} className="text-xs text-indigo-600 hover:underline" data-testid="toggle-quoted-thread">
            {showAll ? "Hide quoted thread" : "Show quoted thread / earlier history"}
          </button>
          {showAll && (
            <div className="bg-slate-50 rounded-lg p-3 border mt-2 text-xs text-slate-500 whitespace-pre-wrap leading-relaxed break-words max-h-96 overflow-y-auto">
              {rest}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Badge({ c, children }) { return <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${c || "bg-slate-100"}`}>{children}</span>; }

function computeTrust(em) {
  const conf = em?.confidence || 0;
  const flags = em?.safety_flags || [];
  const signals = [];
  if (em?.learned_from) signals.push("Learned rule matched");
  if (em?.erp_data_used) signals.push("ERP shipment matched");
  if (em?.reference) signals.push(`Reference ${em.reference}`);
  if (em?.category && !["unknown", "skipped"].includes(em.category)) signals.push(`Category: ${String(em.category).replace(/_/g, " ")}`);
  signals.push(`Confidence ${Math.round(conf * 100)}%`);
  let level = "low";
  if (flags.length) level = "review";
  else if (conf >= 0.9) level = "high";
  else if (conf >= 0.7) level = "medium";
  return { level, conf, flags, signals };
}

const TRUST_META = {
  high:   { label: "High trust",   cls: "bg-emerald-100 text-emerald-700 border-emerald-200", Icon: ShieldCheck },
  medium: { label: "Medium",       cls: "bg-amber-100 text-amber-700 border-amber-200",        Icon: Shield },
  low:    { label: "Low",          cls: "bg-slate-100 text-slate-500 border-slate-200",         Icon: Shield },
  review: { label: "Needs review", cls: "bg-rose-100 text-rose-700 border-rose-200",            Icon: ShieldAlert },
};

function TrustBadge({ email }) {
  const t = computeTrust(email);
  const meta = TRUST_META[t.level];
  const Icon = meta.Icon;
  const title = t.signals.join(" · ") + (t.flags.length ? ` · ⚠ ${t.flags.length} safety flag(s): ${t.flags.join(", ")}` : "");
  return (
    <span data-testid={`trust-badge-${email?.email_id}`} title={title}
      className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${meta.cls}`}>
      <Icon className="h-3 w-3" /> {meta.label}
      {t.level !== "review" && <span className="opacity-70">· {t.signals.length}✓</span>}
    </span>
  );
}
function Empty({ icon: Icon, text, sub }) {
  return <div className="text-center py-8 text-slate-400"><Icon className="h-10 w-10 mx-auto mb-2 opacity-40" /><p className="text-sm">{text}</p>{sub && <p className="text-xs mt-1">{sub}</p>}</div>;
}
function MetricCard({ label, val, icon: Icon, sub }) {
  return <Card><CardContent className="p-3"><div className="flex items-center justify-between"><div><p className="text-xs text-slate-500">{label}</p><p className="text-lg font-bold">{val ?? "—"}</p>{sub && <p className="text-[10px] text-slate-400">{sub}</p>}</div><Icon className="h-5 w-5 text-slate-300" /></div></CardContent></Card>;
}
function ControlButton({ icon: Icon, label, desc, color, onClick, testId }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg border">
      <div><p className="text-sm font-medium">{label}</p><p className="text-xs text-slate-500">{desc}</p></div>
      <Button size="sm" className={`gap-1 ${color} text-white`} onClick={onClick} data-testid={testId}><Icon className="h-4 w-4" /></Button>
    </div>
  );
}
function LifecycleRule({ icon: Icon, text }) {
  return <div className="flex items-center gap-2 py-1.5 border-b border-slate-100 last:border-0"><Icon className="h-4 w-4 text-red-500 flex-shrink-0" /><span className="text-slate-700">{text}</span></div>;
}
function RField({ label, value, onChange, hint }) {
  return <div><label className="text-sm text-slate-600">{label}</label><Input value={value || ""} onChange={e => onChange(e.target.value)} className="text-sm mt-1" />{hint && <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>}</div>;
}
function RRow({ label, value, hl }) {
  return <div className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0"><span className="text-sm text-slate-600">{label}</span><span className={`text-sm font-medium ${hl === "red" ? "text-red-600" : hl === "amber" ? "text-amber-600" : ""}`}>{String(value ?? "—")}</span></div>;
}
function fmtTime(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return ts; }
}
